/*
	This helper function makes a group of elements animate along the x-axis in a seamless, responsive loop.

	Features:
	 - Uses xPercent so that even if the widths change (like if the window gets resized), it should still work in most cases.
	 - When each item animates to the left or right enough, it will loop back to the other side
	 - Optionally pass in a config object with values like draggable: true, center: true, speed (default: 1, which travels at roughly 100 pixels per second), paused (boolean), repeat, reversed, and paddingRight.
	 - The returned timeline will have the following methods added to it:
	   - next() - animates to the next element using a timeline.tweenTo() which it returns. You can pass in a vars object to control duration, easing, etc.
	   - previous() - animates to the previous element using a timeline.tweenTo() which it returns. You can pass in a vars object to control duration, easing, etc.
	   - toIndex() - pass in a zero-based index value of the element that it should animate to, and optionally pass in a vars object to control duration, easing, etc. Always goes in the shortest direction
	   - current() - returns the current index (if an animation is in-progress, it reflects the final index)
	   - times - an Array of the times on the timeline where each element hits the "starting" spot.
	 */
function horizontalLoop(items, config) {
	let timeline;
	items = gsap.utils.toArray(items);
	config = config || {};
	gsap.context(() => { // use a context so that if this is called from within another context or a gsap.matchMedia(), we can perform proper cleanup like the "resize" event handler on the window
		let onChange = config.onChange,
			lastIndex = 0,
			tl = gsap.timeline({repeat: config.repeat, onUpdate: onChange && function() {
					let i = tl.closestIndex();
					if (lastIndex !== i) {
						lastIndex = i;
						onChange(items[i], i);
					}
				}, paused: config.paused, defaults: {ease: "none"}, onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)}),
			length = items.length,
			startX = items[0].offsetLeft,
			times = [],
			widths = [],
			spaceBefore = [],
			xPercents = [],
			curIndex = 0,
			indexIsDirty = false,
			center = config.center,
			pixelsPerSecond = (config.speed || 1) * 100,
			snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1), // some browsers shift by a pixel to accommodate flex layouts, so for example if width is 20% the first element's width might be 242px, and the next 243px, alternating back and forth. So we snap to 5 percentage points to make things look more natural
			timeOffset = 0,
			container = center === true ? items[0].parentNode : gsap.utils.toArray(center)[0] || items[0].parentNode,
			totalWidth,
			getTotalWidth = () => items[length-1].offsetLeft + xPercents[length-1] / 100 * widths[length-1] - startX + spaceBefore[0] + items[length-1].offsetWidth * gsap.getProperty(items[length-1], "scaleX") + (parseFloat(config.paddingRight) || 0),
			populateWidths = () => {
				let b1 = container.getBoundingClientRect(), b2;
				items.forEach((el, i) => {
					widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));
					xPercents[i] = snap(parseFloat(gsap.getProperty(el, "x", "px")) / widths[i] * 100 + gsap.getProperty(el, "xPercent"));
					b2 = el.getBoundingClientRect();
					spaceBefore[i] = b2.left - (i ? b1.right : b1.left);
					b1 = b2;
				});
				gsap.set(items, { // convert "x" to "xPercent" to make things responsive, and populate the widths/xPercents Arrays to make lookups faster.
					xPercent: i => xPercents[i]
				});
				totalWidth = getTotalWidth();
			},
			timeWrap,
			populateOffsets = () => {
				timeOffset = center ? tl.duration() * (container.offsetWidth / 2) / totalWidth : 0;
				center && times.forEach((t, i) => {
					times[i] = timeWrap(tl.labels["label" + i] + tl.duration() * widths[i] / 2 / totalWidth - timeOffset);
				});
			},
			getClosest = (values, value, wrap) => {
				let i = values.length,
					closest = 1e10,
					index = 0, d;
				while (i--) {
					d = Math.abs(values[i] - value);
					if (d > wrap / 2) {
						d = wrap - d;
					}
					if (d < closest) {
						closest = d;
						index = i;
					}
				}
				return index;
			},
			populateTimeline = () => {
				let i, item, curX, distanceToStart, distanceToLoop;
				tl.clear();
				for (i = 0; i < length; i++) {
					item = items[i];
					curX = xPercents[i] / 100 * widths[i];
					distanceToStart = item.offsetLeft + curX - startX + spaceBefore[0];
					distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");
					tl.to(item, {xPercent: snap((curX - distanceToLoop) / widths[i] * 100), duration: distanceToLoop / pixelsPerSecond}, 0)
						.fromTo(item, {xPercent: snap((curX - distanceToLoop + totalWidth) / widths[i] * 100)}, {xPercent: xPercents[i], duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond, immediateRender: false}, distanceToLoop / pixelsPerSecond)
						.add("label" + i, distanceToStart / pixelsPerSecond);
					times[i] = distanceToStart / pixelsPerSecond;
				}
				timeWrap = gsap.utils.wrap(0, tl.duration());
			},
			refresh = (deep) => {
				let progress = tl.progress();
				tl.progress(0, true);
				populateWidths();
				deep && populateTimeline();
				populateOffsets();
				deep && tl.draggable && tl.paused() ? tl.time(times[curIndex], true) : tl.progress(progress, true);
			},
			onResize = () => refresh(true),
			proxy;
		gsap.set(items, {x: 0});
		populateWidths();
		populateTimeline();
		populateOffsets();
		window.addEventListener("resize", onResize);
		function toIndex(index, vars) {
			vars = vars || {};
			(Math.abs(index - curIndex) > length / 2) && (index += index > curIndex ? -length : length); // always go in the shortest direction
			let newIndex = gsap.utils.wrap(0, length, index),
				time = times[newIndex];
			if (time > tl.time() !== index > curIndex && index !== curIndex) { // if we're wrapping the timeline's playhead, make the proper adjustments
				time += tl.duration() * (index > curIndex ? 1 : -1);
			}
			if (time < 0 || time > tl.duration()) {
				vars.modifiers = {time: timeWrap};
			}
			curIndex = newIndex;
			vars.overwrite = true;
			gsap.killTweensOf(proxy);
			return vars.duration === 0 ? tl.time(timeWrap(time)) : tl.tweenTo(time, vars);
		}
		tl.toIndex = (index, vars) => toIndex(index, vars);
		tl.closestIndex = setCurrent => {
			let index = getClosest(times, tl.time(), tl.duration());

			if (setCurrent) {
				curIndex = index;
				indexIsDirty = false;
			}
			return index;
		};
		tl.config = config;
		tl.refresh = () => refresh(true);
		tl.dispose = () => { window.removeEventListener("resize", onResize) };
		tl.current = () => indexIsDirty ? tl.closestIndex(true) : curIndex;
		tl.next = vars => toIndex(tl.current()+1, vars);
		tl.previous = vars => toIndex(tl.current()-1, vars);
		tl.times = times;
		tl.progress(1, true).progress(0, true); // pre-render for performance
		if (config.reversed) {
			tl.vars.onReverseComplete();
			tl.reverse();
		}
		if (config.draggable && typeof(Draggable) === "function") {
			proxy = document.createElement("div")
			let wrap = gsap.utils.wrap(0, 1),
				ratio, startProgress, draggable, dragSnap, lastSnap, initChangeX, wasPlaying,
				align = () => tl.progress(wrap(startProgress + (draggable.startX - draggable.x) * ratio)),
				syncIndex = () => tl.closestIndex(true);
			typeof(InertiaPlugin) === "undefined" && console.warn("InertiaPlugin");
			draggable = Draggable.create(proxy, {
				trigger: items[0].parentNode,
				type: "x",
				onPressInit() {
					let timestamp = performance.now();
					let oldX = this.x;
					let wasThrowingBefore = this.isThrowing;
					gsap.killTweensOf(tl);
					wasPlaying = !tl.paused();
					tl.pause();
					startProgress = tl.progress();
					refresh();
					ratio = 1 / totalWidth;

					// Set proxy.x FIRST before reading this.x to prevent extreme initChangeX values
					// Use explicit "px" unit to override global gsap.config({units: {x:"vw"}})
					let calculatedX = startProgress / -ratio;
					gsap.set(proxy, {x: calculatedX + "px"});
					this.update();  // Force Draggable to sync its internal x value
					let x = this.x;

					let difference = oldX - x;
					let didLoopWrap = Math.abs(difference) > totalWidth / 2;  // Detect loop wrap

					initChangeX = (startProgress / -ratio) - x;
				},
				onDrag: align,
				onThrowUpdate: align,
				overshootTolerance: 0,
				inertia: true,
				throwResistance: 1000,  // Lower resistance for more natural feel (default: 1000)
				maxDuration: 0.8,  // Max 0.8 second throw duration
				minDuration: 0.2,  // Min 0.2 seconds (default)
				velocityScale: 0.6,  // Reduce velocity to prevent extreme throws (default: 1)
				snap(value) {
					//note: if the user presses and releases in the middle of a throw, due to the sudden correction of proxy.x in the onPressInit(), the velocity could be very large, throwing off the snap. So sense that condition and adjust for it. We also need to set overshootTolerance to 0 to prevent the inertia from causing it to shoot past and come back
					let timestamp = performance.now();
					//console.log("🟡 snap [" + timestamp.toFixed(2) + "ms]: value=" + value + ", startProgress=" + startProgress + ", ratio=" + ratio + ", thisX=" + this.x);

					let dragDistance = draggable.startX - draggable.x;
					let actualDistance = Math.abs(dragDistance);
					let checkValue = Math.abs(startProgress / -ratio - this.x);

					if (checkValue < 10) {
						let earlyReturnTime = performance.now();
						/*console.log("🟡 snap - early return [" + earlyReturnTime.toFixed(2) + "ms]: " + [
							"value=" + value,
							"dragDistance=" + dragDistance,
							"actualDistance=" + actualDistance,
							"thisX=" + this.x,
							"startX=" + draggable.startX,
							"checkValue=" + checkValue,
							"startProgress=" + startProgress,
							"ratio=" + ratio,
							"lastSnap=" + lastSnap,
							"initChangeX=" + initChangeX,
							"returning=" + (lastSnap + initChangeX)
						].join(", "));*/
						return lastSnap + initChangeX
					}

					// CRITICAL FIX 1: If initChangeX is very large (loop wrap during throw),
					// the velocity will be inflated. Detect and correct this.
					// LOWERED THRESHOLD from 100 to 50 to catch more cases
					let adjustedValue = value;
					let wasInitChangeXAdjusted = false;
					if (Math.abs(initChangeX) > 50) {
						// Large initChangeX means we wrapped during a throw
						// Use a more conservative value based on actual drag distance
						adjustedValue = draggable.startX - this.x;
						wasInitChangeXAdjusted = true;
						//console.log("⚠️ LARGE initChangeX DETECTED: " + initChangeX + ", using adjustedValue=" + adjustedValue + " instead of " + value);
					}

					// Calculate valueVsDistance for logging purposes
					let valueVsDistance = actualDistance > 0 ? Math.abs(adjustedValue / actualDistance) : 0;

					let time = -(adjustedValue * ratio) * tl.duration(),
						wrappedTime = timeWrap(time),
						closestIndex = getClosest(times, wrappedTime, tl.duration()),
						snapTime = times[closestIndex],
						dif = snapTime - wrappedTime;

					// Check if we're wrapping around (crossing the loop boundary)
					let wasAdjusted = Math.abs(dif) > tl.duration() / 2;
					if (wasAdjusted) {
						dif += dif < 0 ? tl.duration() : -tl.duration();
					}

					lastSnap = (time + dif) / tl.duration() / -ratio;

					let snapEndTime = performance.now();
					/*console.log("🟢 snap [" + snapEndTime.toFixed(2) + "ms]: " + [
						"value=" + value,
						"adjustedValue=" + adjustedValue,
						"dragDistance=" + dragDistance,
						"actualDistance=" + actualDistance,
						"thisX=" + this.x,
						"startX=" + draggable.startX,
						"time=" + time,
						"wrappedTime=" + wrappedTime,
						"closestIndex=" + closestIndex,
						"snapTime=" + snapTime,
						"dif=" + dif,
						"wasAdjusted=" + wasAdjusted,
						"lastSnap=" + lastSnap,
						"tlDuration=" + tl.duration(),
						"tlProgress=" + tl.progress(),
						"valueVsDistance=" + valueVsDistance,
						"initChangeX=" + initChangeX,
						"wasInitChangeXAdjusted=" + wasInitChangeXAdjusted
					].join(", "));*/
					return lastSnap;
				},
				onRelease() {
					let timestamp = performance.now();
					syncIndex();
					/*console.log("🔴 onRelease [" + timestamp.toFixed(2) + "ms]: " + [
						"isThrowing=" + draggable.isThrowing,
						"x=" + draggable.x,
						"startX=" + draggable.startX,
						"dragDistance=" + (draggable.startX - draggable.x)
					].join(", "));*/
					draggable.isThrowing && (indexIsDirty = true);
				},
				onThrowComplete: () => {
					let timestamp = performance.now();
					/*console.log("🟣 onThrowComplete [" + timestamp.toFixed(2) + "ms]: " + [
						"finalX=" + draggable.x,
						"progress=" + tl.progress()
					].join(", "));*/
					syncIndex();
					//wasPlaying && tl.play();
					config.complete && config.complete();
				}
			})[0];
			tl.draggable = draggable;
		}
		tl.closestIndex(true);
		lastIndex = curIndex;
		onChange && onChange(items[curIndex], curIndex);
		timeline = tl;
		return () => window.removeEventListener("resize", onResize); // cleanup
	});
	return timeline;
}

const clearTimeline = (...timelines) => {
	timelines.forEach((tl) => {
		if (!tl) return

		tl.kill();
		tl.progress(0).kill()
		let childrens = tl.getChildren(true, true, false)

		childrens.forEach((child) => {
			gsap.set(child._targets,{clearProps:true});
			child.kill();
		})
	})
}

/**
 * Provides infinite scrolling behavior using GSAP horizontal loop.
 *
 * @property {import('vue').Component} component - The Vue component instance.
 * @property {HTMLElement} lane - The lane element.
 * @property {HTMLElement[]} originalChilds - Original lane items before cloning.
 * @property {HTMLElement[]} currentChilds - All current lane items including clones.
 * @property {HTMLElement|null} activeElement - Currently active (centered) item.
 * @property {GSAPTimeline|null} loop - GSAP loop timeline instance.
 * @method init - Sets up the plugin and handles responsive rebuilds.
 * @method populateBoxes - Clones lane items until overflow is sufficient.
 * @method initBoxes - Initializes GSAP loop and event bindings.
 * @method disposeBoxes - Cleans up GSAP loop and removes clones.
 * @method bindEvents - Adds event listeners for hover and click.
 * @method unbindEvents - Removes previously bound event listeners.
 * @method onMouseEnter - Slows down the loop on hover.
 * @method onMouseLeave - Resumes normal loop speed on mouse leave.
 * @method complete - Callback after tween completes.
 * @method next - Go to next item.
 * @method prev - Go to previous item.
 * @method play - Placeholder method.
 * @method pause - Placeholder method.
 * @method isPaused - Placeholder method.
 * @method destroy - Disposes the whole lane logic.
 */
export default class LaneInfinityX {
	constructor(component) {
		//console.clear();
		this.component = component;
		this.lane = this.component.lane;
		this.loop = null;
		this.itemCounter = 0;
		this.index = 0;
		this.realIndex = 0;
		this.originalChilds = [...gsap.utils.toArray(this.component.childs)];
		this.currentChilds = [];
		this.activeItem = null;
		this.autoScrollStop = false;

		this.init();
	}

	init() {
		gsap.registerPlugin(Draggable);
		gsap.registerPlugin(InertiaPlugin);

		this.originalChilds.forEach((origBox, i) => {
			origBox.dataset.laneIndex = i;
			origBox.dataset.laneRealIndex = this.itemCounter++;
		});

		this.populateBoxes();

		let resizeTimeout;
		window.addEventListener("resize", () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(() => {
				this.populateBoxes();
			}, 250);
		});
	}

	populateBoxes() {
		const wrapperWidth = this.lane.offsetWidth;

		if(this.currentChilds.length === 0)
		{
			this.currentChilds = [...gsap.utils.toArray(this.component.childs)];
			this.initBoxes();
		}

		//console.log("populateBoxes:",this.currentChilds[0]);
		const boxWidth = this.currentChilds[0].offsetWidth;
		let totalWidth = this.currentChilds.reduce((sum, box) => sum + box.offsetWidth, 0);
		let itemsCount = this.currentChilds.length;

		while (totalWidth < wrapperWidth + boxWidth) {
			this.originalChilds.forEach((origBox, i) => {
				const clone = origBox.cloneNode(true);
				clone.dataset.laneIndex = i;
				clone.dataset.laneRealIndex = this.itemCounter++;
				this.lane.appendChild(clone);
				this.currentChilds.push(clone);
				totalWidth += clone.offsetWidth;
			});
		}


		if(itemsCount < this.currentChilds.length)
		{
			this.disposeBoxes();
			this.initBoxes();
		}
	}

	disposeBoxes()
	{
		this.unbindEvents();

		if (this.loop) {
				this.loop.dispose();
				clearTimeline(this.loop);
				this.loop = null;
		}

		if(this.activeElement)
		{
			this.activeElement = null;
		}

		this.lane.querySelectorAll(".infinity-active").forEach(el => el.classList.remove("infinity-active"));
	}

	bindEvents() {
		const lane = this.component.lane;
		this.onMouseEnter = this.onMouseEnter.bind(this);
		this.onMouseLeave = this.onMouseLeave.bind(this);
		this.complete = this.complete.bind(this);

		if(this.component.autoScrollItemHoverPause)
		{
			lane.addEventListener('mouseenter', this.onMouseEnter);
			lane.addEventListener('mouseleave', this.onMouseLeave);
		}

		if(this.component.itemsClickToCenterEnabled)
		{
			this.currentChilds.forEach((box, i) => {
				box._clickHandler = () => this.loop.toIndex(i, {duration: .9, ease: "power3.out", onComplete: this.complete});
				box.addEventListener("click", box._clickHandler);
			});
		}
	}

	unbindEvents()
	{
		const lane = this.component.lane;
		lane.removeEventListener('mouseenter', this.onMouseEnter);
		lane.removeEventListener('mouseleave', this.onMouseLeave);

		this.currentChilds.forEach((box, i) => {
			box.removeEventListener("click", box._clickHandler);
			delete box._clickHandler;
		});
	}

	initBoxes()
	{
		this.loop = horizontalLoop(this.currentChilds, {
			repeat: -1,
			speed: this.component.autoScrollSpeed * .1,
			reversed: this.component.autoScrollReversed,
			paused: !this.component.autoScroll,
			draggable: true, // make it draggable
			center: true, // active element is the one in the center of the container rather than th left edge
			onChange: (element, index) => { // when the active element changes, this function gets called.
				const laneIndex = element.dataset.laneIndex;
				const realIndex = element.dataset.laneRealIndex;
				//console.log("onChange => loopIndex:", index, "laneIndex:", laneIndex, "realIndex:", realIndex);

				this.activeElement && this.activeElement.classList.remove("infinity-active");
				element.classList.add("infinity-active");
				this.activeElement = element;
				this.index = laneIndex;
				this.realIndex = realIndex;
			},
			complete: () => this.complete()
		});

		this.bindEvents();
	}

	onMouseEnter() {
		this.component.autoScrollItemIsPausedMouseOver = true;

		if(this.autoScrollStop)
			return;

		gsap.to(this.loop, { timeScale: 0, duration: 1.3, ease: 'power2.out'});
	}

	onMouseLeave() {
		this.component.autoScrollItemIsPausedMouseOver = false;

		if(this.autoScrollStop)
			return;

		//console.log("leave:",this.component.autoScrollItemIsPausedMouseOver,this.component.autoScrollItemHoverPause);

		if(this.component.autoScrollItemHoverPause)
		{
			if (this.component.autoScrollReversed)
			{
				gsap.to(this.loop, { timeScale: -1, duration: 1.3, ease: "power2.out"});
			}
			else
			{
				gsap.to(this.loop, { timeScale: 1, duration: 1.3, ease: "power2.out"});
			}
		}

	}

	getRealIndex()
	{
		return this.realIndex;
	}

	getIndex()
	{
		return this.index;
	}

	complete()
	{
		if (this.component.autoScroll) {
			if (this.component.autoScrollReversed) {
				this.loop.reverse();
			} else {
				this.loop.play();
			}
		}
	}

	next() {
		if (this.loop) this.loop.next({ duration: .9, ease: "power3.out", onComplete: this.complete });
	}

	prev() {
		if (this.loop) this.loop.previous({ duration: .9, ease: "power3.out", onComplete: this.complete });
	}

	play()
	{

	}

	pause()
	{

	}

	isPaused() {

	}

	startAutoscroll() {
		this.autoScrollStop = false;
	}

	stopAutoscroll() {
		this.autoScrollStop = true;
	}

	destroy() {
		this.disposeBoxes();
	}
}
